import { Challenge, PlayerChallenge } from "types/challenge";
import { User } from "types/user";
import { ManagerJourneyStage, filterChallengesByJourneyProfile } from "utils/journeyProfile";
import { api } from "./api";
import { dedupedFetch } from "./cache";

interface FetchChallengesWithStatusOptions {
  user?: User;
  managerStage?: ManagerJourneyStage;
}

/**
 * Busca todos os desafios disponíveis na plataforma
 */
const fetchChallenges = async (): Promise<Challenge[]> => {
  return dedupedFetch("challenges", async () => {
    const { data } = await api.get("/challenge");
    return data;
  });
};

/**
 * Busca os desafios conquistados pelo jogador atual
 * Retorna informações sobre quais desafios foram completados
 * @param playerId - ID do jogador para buscar o status
 */
const fetchPlayerChallenges = async (playerId: string): Promise<PlayerChallenge[]> => {
  return dedupedFetch(`player-challenges-${playerId}`, async () => {
    const { data } = await api.get(`/player/me/status`);

    const completedChallengeIds: string[] = [];

    if (data && data.challenges) {
      Object.keys(data.challenges).forEach(challengeId => {
        completedChallengeIds.push(challengeId);
      });
    }

    return completedChallengeIds.map(id => ({
      id,
      name: '',
      description: '',
      earned: true
    }));
  });
};

/**
 * Verifica se um desafio específico foi conquistado pelo jogador
 * @param playerId - ID do jogador
 * @param challengeId - ID do desafio a verificar
 */
const isChallengeCompleted = async (playerId: string, challengeId: string): Promise<boolean> => {
  try {
    const playerChallenges = await fetchPlayerChallenges(playerId);
    return playerChallenges.some(ch => ch.id === challengeId);
  } catch (error) {
    console.error("Erro ao verificar status do desafio:", error);
    return false;
  }
};

/**
 * Combina os dados dos desafios disponíveis com os conquistados pelo jogador
 * Retorna uma lista completa com o status de cada desafio
 * @param playerId - ID do jogador para verificar os desafios completados
 */
const fetchChallengesWithStatus = async (
  playerId: string,
  options: FetchChallengesWithStatusOptions = {}
) => {
  try {
    const [challenges, playerChallenges] = await Promise.all([
      fetchChallenges(),
      fetchPlayerChallenges(playerId)
    ]);

    const earnedChallengeIds = new Set(playerChallenges.map(pc => pc.id));

    const challengesWithStatus = challenges.map(challenge => ({
      id: challenge._id,
      titulo: challenge.challenge,
      descricao: challenge.description,
      concluida: earnedChallengeIds.has(challenge._id),
      icone: challenge.badgeUrl || challenge.badge?.original?.url,
      badge: challenge.badge,
      _raw: challenge
    }));

    return filterChallengesByJourneyProfile(
      challengesWithStatus,
      options.user,
      options.managerStage
    );
  } catch (error: any) {
    console.error("Erro ao buscar desafios com status:", error);
    throw error;
  }
};

export {
  fetchChallenges, fetchChallengesWithStatus, fetchPlayerChallenges,
  isChallengeCompleted
};

